<template>
  <Transition name="bg-fade" @enter="handleOpenSlideover">
    <SbBlokUi v-if="openBlokUI" @mousedown.self="handlePreventClose">
      <Transition :name="returnAnimationName" @leave="handleCloseSlide">
        <div
          v-if="openSlideover"
          ref="blok"
          v-focus-trap="hasFocusTrap && isOpen"
          role="dialog"
          class="sb-slideover"
          :class="{ 'sb-slideover--left': orientation === 'left' }"
        >
          <SbIconButton
            class="sb-slideover__close-button"
            icon="x"
            variant="quaternary"
            is-clear
            size="small"
            label="Close Slideover"
            type="button"
            @click="handlePreventClose"
          />

          <slot />
        </div>
      </Transition>
    </SbBlokUi>
  </Transition>
</template>

<script lang="ts" setup>
import { ref, watch, computed, nextTick } from 'vue'
import SbBlokUi from '../BlockUI'
import SbIconButton from '../IconButton'
import { FocusTrap as vFocusTrap } from '../../directives'

export type SbSlideoverProps = {
  isOpen: boolean
  orientation?: string
  preventClose?: boolean
  hasFocusTrap?: boolean
}

const props = withDefaults(defineProps<SbSlideoverProps>(), {
  isOpen: false,
  orientation: 'right',
  preventClose: false,
  hasFocusTrap: true,
})

const emit = defineEmits<{
  (e: 'hide'): void
  (e: 'prevent-closing'): void
  (e: 'show'): void
}>()

const openBlokUI = ref(props.isOpen)
const openSlideover = ref(false)
const blok = ref<HTMLElement | null>(null)

const returnAnimationName = computed(() =>
  props.orientation === 'right' ? 'slide-right-fade' : 'slide-left-fade',
)

watch(
  () => props.isOpen,
  (state) => {
    if (state) {
      handleOpenSlide()
    } else {
      openSlideover.value = false
    }
  },
  { immediate: true },
)

watch(
  () => props.preventClose,
  (state) => {
    if (!state) {
      openSlideover.value = state
    }
  },
)

function handlePreventClose() {
  if (props.preventClose) {
    emit('prevent-closing')
  } else {
    openSlideover.value = false
  }
}

function handleCloseSlide() {
  if (openBlokUI.value) {
    openBlokUI.value = false
    nextTick(() => {
      emit('hide')

      removeShortCuts()
    })
  }
}

function handleOpenSlide() {
  if (!openBlokUI.value) {
    openBlokUI.value = true
    nextTick(() => {
      emit('show')

      registerShortCuts()
    })
  }
}

function handleOpenSlideover() {
  openSlideover.value = true
}

function handleKeyDown(e: KeyboardEvent) {
  if (e.key === 'Escape' || e.key === 'Esc') {
    handlePreventClose()
  }
}

function registerShortCuts() {
  window.addEventListener('keydown', handleKeyDown)
}

function removeShortCuts() {
  window.removeEventListener('keydown', handleKeyDown)
}

defineExpose({
  /**
   * Force the slideover to close, and check if the preventClose prop is true
   * if it is true, it will emit the prevent-closing event
   * @returns void
   */
  handlePreventClose,
})
</script>
